import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';

export type Formato = 'pdf' | 'xlsx';

/**
 * Baja un informe y lo entrega al navegador.
 *
 * <p>Los informes no se abren con un enlace normal: la petición va con el token de la sesión en
 * la cabecera, así que hay que pedirla por fetch y armar la descarga con el blob que vuelve.
 *
 * <p>El nombre del fichero lo pone la pantalla y no el servidor, porque en la carpeta de
 * descargas acaban diez informes del mismo tipo y lo que los distingue es el período.
 */
export async function descargarInforme(ruta: string, nombre: string, queEs: string): Promise<void> {
  try {
    const fichero = await api.descargar(ruta);
    if (!fichero) {
      notificar.error('NO SE PUDO GENERAR EL FICHERO');
      return;
    }

    const url = URL.createObjectURL(fichero);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    enlace.click();
    URL.revokeObjectURL(url);

    notificar.exito(`${queEs} descargado`);
  } catch (causa) {
    notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR');
  }
}

/** Los dos botones de siempre, en PDF y en Excel, con el que esté bajando girando. */
export function BotonesDeDescarga({
  bajando,
  alDescargar,
  pdf,
  xlsx,
}: {
  bajando: Formato | null;
  alDescargar: (formato: Formato) => void;
  pdf: string;
  xlsx: string;
}) {
  return (
    <span className="flex items-center gap-2">
      <Descarga
        formato="pdf"
        icono={FileText}
        titulo={pdf}
        bajando={bajando}
        alPulsar={() => alDescargar('pdf')}
        clase="hover:border-red-300 hover:text-red-600"
      />
      <Descarga
        formato="xlsx"
        icono={FileSpreadsheet}
        titulo={xlsx}
        bajando={bajando}
        alPulsar={() => alDescargar('xlsx')}
        clase="hover:border-emerald-300 hover:text-emerald-600"
      />
    </span>
  );
}

export function Descarga({
  formato,
  icono: Icono,
  titulo,
  bajando,
  alPulsar,
  clase,
}: {
  formato: Formato;
  icono: typeof FileText;
  titulo: string;
  bajando: Formato | null;
  alPulsar: () => void;
  clase: string;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={bajando !== null}
      title={titulo}
      aria-label={titulo}
      className={`grid size-10 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 transition-colors disabled:opacity-40 ${clase}`}
    >
      {bajando === formato ? (
        <Loader2 className="size-[18px] animate-spin" aria-hidden />
      ) : (
        <Icono className="size-[18px]" aria-hidden />
      )}
    </button>
  );
}
